import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Lock, 
  User, 
  Mail, 
  Eye, 
  EyeOff, 
  AlertCircle, 
  Pickaxe,
  Shield,
  ArrowLeft
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CryptoLogin = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ 
    username: '', 
    password: '', 
    email: '',
    totp_code: '' 
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/crypto/login`, {
          username: formData.username,
          password: formData.password,
          totp_code: formData.totp_code || undefined
        });
        
        if (response.data.requires_2fa) {
          setRequires2FA(true);
          setIsLoading(false);
          return;
        }
        
        localStorage.setItem('cryptoToken', response.data.token);
        localStorage.setItem('cryptoUsername', response.data.username);
        navigate('/crypto/dashboard');
      } else {
        // Register
        await axios.post(`${API}/crypto/register`, {
          username: formData.username,
          email: formData.email,
          password: formData.password
        });
        
        // Auto login after registration
        const loginResponse = await axios.post(`${API}/crypto/login`, {
          username: formData.username,
          password: formData.password
        });
        
        localStorage.setItem('cryptoToken', loginResponse.data.token);
        localStorage.setItem('cryptoUsername', loginResponse.data.username);
        navigate('/crypto/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 px-4" data-testid="crypto-login-page">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-yellow-500/10 rounded-full blur-3xl animate-pulse delay-500" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        {/* Back button */}
        <a 
          href="/admin/dashboard" 
          className="absolute -top-12 left-0 flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={18} />
          Back to Admin
        </a>

        <div className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-8 border border-gray-700 shadow-2xl">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-purple-600 to-cyan-600 mb-4">
              <Pickaxe className="text-white" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">CryptoMiner Dashboard</h1>
            <p className="text-gray-400">
              {isLogin ? 'Sign in to your account' : 'Create your account'}
            </p>
          </div>

          {/* Toggle Login/Register */}
          <div className="flex bg-gray-900 rounded-xl p-1 mb-6">
            <button
              onClick={() => { setIsLogin(true); setError(''); setRequires2FA(false); }}
              className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                isLogin ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => { setIsLogin(false); setError(''); setRequires2FA(false); }}
              className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                !isLogin ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Register
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 p-4 mb-6 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300"
            >
              <AlertCircle size={18} />
              <span>{error}</span>
            </motion.div>
          )}

          {/* 2FA Required Message */}
          {requires2FA && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 p-4 mb-6 bg-yellow-500/20 border border-yellow-500/30 rounded-xl text-yellow-300"
            >
              <Shield size={18} />
              <span>Enter your 2FA code from Google Authenticator</span>
            </motion.div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="Enter username"
                  className="pl-12 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500"
                  data-testid="crypto-username"
                  required
                />
              </div>
            </div>

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email"
                    className="pl-12 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500"
                    data-testid="crypto-email"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  className="pl-12 pr-12 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500"
                  data-testid="crypto-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {requires2FA && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">2FA Code</label>
                <div className="relative">
                  <Shield className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                  <Input
                    type="text"
                    value={formData.totp_code}
                    onChange={(e) => setFormData({ ...formData, totp_code: e.target.value })}
                    placeholder="Enter 6-digit code"
                    className="pl-12 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500 tracking-widest text-center text-lg"
                    data-testid="crypto-2fa"
                    maxLength={6}
                    required={requires2FA}
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full py-6 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-300"
              data-testid="crypto-submit-button"
            >
              {isLoading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          {/* Security note */}
          <div className="mt-6 p-4 bg-gray-900/50 rounded-xl border border-gray-700">
            <div className="flex items-start gap-3">
              <Shield className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-gray-400">
                <p className="font-medium text-gray-300 mb-1">Security First</p>
                <p>Your API keys are encrypted and stored securely. Enable 2FA after login for extra protection.</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CryptoLogin;
